import React from 'react';
import { Search, ChevronRight, UserCheck, ExternalLink, ShieldCheck, Settings } from 'lucide-react';
import { MentorAreaConfig } from '../../types/mentorship';
import { useAuth } from '../../contexts/AuthContext';
import { UserMenu } from '../AuthComponents';

interface MentorshipTopbarProps {
  config: MentorAreaConfig;
  breadcrumbs?: { label: string; onClick?: () => void }[];
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onOpenAffiliationModal?: () => void;
  onOpenAdminPanel?: () => void;
  onOpenAuth?: () => void;
}

export const MentorshipTopbar: React.FC<MentorshipTopbarProps> = ({
  config,
  breadcrumbs = [],
  searchQuery,
  onSearchChange,
  onOpenAffiliationModal,
  onOpenAdminPanel,
  onOpenAuth,
}) => {
  const { user, profile, isMentor, isAdmin, signOut } = useAuth();
  const accentColor = config.branding.accentColor || '#0FFF55';

  return (
    <header className="sticky top-0 z-30 w-full bg-[#121316]/90 backdrop-blur-md border-b border-white/5 px-4 md:px-10 py-3.5 flex items-center justify-between gap-4 select-none">
      {/* Left: Breadcrumbs (1.00 Badge Removed) */}
      <div className="flex items-center gap-3 overflow-hidden text-xs text-gray-400">
        <div className="flex items-center gap-2 truncate">
          <span className="font-semibold text-white truncate">{config.courseTitle}</span>
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={idx}>
              <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              <button
                onClick={crumb.onClick}
                className={`truncate hover:text-white transition-colors ${
                  idx === breadcrumbs.length - 1 ? 'text-gray-200 font-medium' : 'text-gray-400'
                }`}
              >
                {crumb.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Right: Mentor Card, Admin Button, Search, Profile */}
      <div className="flex items-center gap-3">
        {/* Mentor Pill (Hubla Style) */}
        <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1c1e24] border border-white/10">
          <img
            src={config.mentorAvatarUrl}
            alt={config.mentorName}
            className="w-6 h-6 rounded-full object-cover ring-1 ring-white/20"
          />
          <span className="text-xs font-semibold text-gray-200">{config.mentorName}</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#0FFF55] animate-pulse" />
        </div>

        {/* Botão Painel do Mentor (Exibido apenas para o mentor responsável ou admin geral) */}
        {(isMentor || isAdmin) && onOpenAdminPanel && (
          <button
            onClick={onOpenAdminPanel}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold transition-all active:scale-95 shadow-md"
          >
            <Settings className="w-3.5 h-3.5" />
            <span>Painel do Mentor</span>
          </button>
        )}

        {/* Affiliation / Action Button */}
        <button
          onClick={onOpenAffiliationModal}
          className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1c1e24] hover:bg-white/10 text-gray-200 text-xs font-semibold border border-white/10 transition-all active:scale-95"
        >
          <UserCheck className="w-3.5 h-3.5" style={{ color: accentColor }} />
          <span>Afiliar-se</span>
          <ExternalLink className="w-3 h-3 text-gray-400" />
        </button>

        {/* Search Bar */}
        <div className="relative flex items-center">
          <Search className="absolute left-3 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar..."
            className="w-32 sm:w-48 md:w-60 pl-8 pr-3 py-1.5 bg-[#17181c] border border-white/10 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none transition-all"
          />
        </div>

        {/* User Profile Avatar / Menu Funcional */}
        {user && profile ? (
          <UserMenu
            user={user}
            profile={profile}
            isAdmin={isAdmin}
            onSignOut={signOut}
          />
        ) : (
          <button
            onClick={onOpenAuth}
            className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border border-white/20 flex items-center justify-center text-xs font-bold text-white shadow-md hover:border-[#0FFF55] transition-colors"
          >
            Entrar
          </button>
        )}
      </div>
    </header>
  );
};
